import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { ArrowLeft } from "lucide-react";

const OR = "#d4af37";
const serif = { fontFamily: "'Cormorant Garamond', Georgia, 'Times New Roman', serif" } as const;
const IMG = "/images/laboratoire/";

/** Pictogrammes premium : trait fin doré, dessinés pour Grafton IA. */
function Picto({ name }: { name: string }) {
  const p = { fill: "none", stroke: "currentColor", strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  const t = { ...p, strokeWidth: 1.3 };
  const f = { ...p, strokeWidth: 0.7, opacity: 0.55 };
  const svg = (children: React.ReactNode) => <svg viewBox="0 0 64 64" className="h-full w-full">{children}</svg>;
  switch (name) {
    case "tables":
      return svg(<><rect x="8" y="26" width="48" height="6" rx="1.5" {...t} /><path d="M14 32v20M50 32v20" {...t} /><rect x="16" y="12" width="14" height="10" rx="1.5" {...t} /><rect x="36" y="12" width="14" height="10" rx="1.5" {...t} /><path d="M23 22v4M43 22v4" {...f} /><path d="M6 52h52" {...f} /></>);
    case "business":
      return svg(<><path d="M32 8c9 6 13 16 11 28l-11 9-11-9C19 24 23 14 32 8z" {...t} /><circle cx="32" cy="22" r="4" {...t} /><path d="M21 36 12 45l10-2M43 36l9 9-10-2" {...t} /><path d="M27 51l5 8 5-8" {...t} /><path d="M32 12v3" {...f} /></>);
    case "bar":
      return svg(<><ellipse cx="32" cy="22" rx="22" ry="7" {...t} /><path d="M10 22v8c0 4 10 7 22 7s22-3 22-7v-8" {...t} /><path d="M20 40v14M44 40v14M14 54h12M38 54h12" {...t} /><path d="M24 12c1-3 3-3 4-6M34 12c1-3 3-3 4-6" {...f} /></>);
    case "academie":
      return svg(<><path d="M32 10 6 22l26 12 26-12z" {...t} /><path d="M16 28v14c0 5 7 9 16 9s16-4 16-9V28" {...t} /><path d="M58 22v16" {...t} /><circle cx="58" cy="41" r="2" {...t} /><path d="M32 34v8" {...f} /></>);
    case "lab":
      return svg(<><path d="M24 8h16M27 8v18L12 50c-2 4 0 8 5 8h30c5 0 7-4 5-8L37 26V8" {...t} /><path d="M18 42h28" {...f} /><circle cx="28" cy="48" r="2" {...t} /><circle cx="38" cy="46" r="1.5" {...t} /><path d="M46 12l3-3M50 18h4" {...f} /></>);
    case "tokens":
      return svg(<><ellipse cx="26" cy="16" rx="14" ry="5" {...t} /><path d="M12 16v8c0 3 6 5 14 5s14-2 14-5v-8" {...t} /><path d="M12 24v8c0 3 6 5 14 5s14-2 14-5v-8" {...t} /><ellipse cx="42" cy="44" rx="14" ry="5" {...t} /><path d="M28 44v8c0 3 6 5 14 5s14-2 14-5v-8" {...t} /><path d="M42 40v8M39 42h5" {...f} /></>);
    default:
      return svg(<circle cx="32" cy="32" r="20" {...t} />);
  }
}

interface Espace { n: string; titre: string; picto: string; texte: string; image?: string; legende?: string }
const ESPACES: Espace[] = [
  { n: "01", titre: "AI Discovery Tables", picto: "tables", image: "espace-hall.jpg", legende: "Les grandes tables du hall : on essaie, on compare, on repart avec un résultat.",
    texte: "De grandes tables en bois clair pour tester ChatGPT, Claude, Gemini, les outils vidéo et les agents IA, et comparer leurs performances sur des cas concrets." },
  { n: "02", titre: "Create Your Business", picto: "business", image: "espace-pitch.jpg", legende: "Le Pitch Studio : de l'idée à la présentation devant investisseurs.",
    texte: "Un espace où un visiteur peut repartir avec un concept d'entreprise, une étude de marché, un business plan, un logo, un site internet et un plan de lancement." },
  { n: "03", titre: "Grafton Genius Bar", picto: "bar", image: "espace-bar.jpg", legende: "Le bar central : conseils d'experts, optimisation de prompts, lancement de projet.",
    texte: "Un comptoir avec des experts pour obtenir des conseils, optimiser ses prompts, automatiser son entreprise et résoudre des problèmes avec l'IA." },
  { n: "04", titre: "AI Academy", picto: "academie", image: "espace-academy.jpg", legende: "Les ateliers : on apprend en pratiquant, en petit groupe.",
    texte: "Des ateliers de 30 à 90 minutes : créer son premier site, automatiser sa comptabilité, produire une publicité ou découvrir les agents autonomes." },
  { n: "05", titre: "Frontier AI Lab", picto: "lab", image: "espace-tableau.jpg", legende: "Le tableau intelligent : écriture, schémas et plans générés par l'IA.",
    texte: "Un mur interactif consacré aux dernières avancées : modèles de pointe, robotique, IA multimodale et démonstrations expérimentales." },
  { n: "06", titre: "AI Token & Services Bar", picto: "tokens", image: "espace-tokens.jpg", legende: "Équipements, abonnements, crédits et services réunis sur un grand mur.",
    texte: "Un guichet pour choisir des abonnements, comparer les coûts des modèles et acheter des crédits d'utilisation, avec des tarifs négociés lorsque les accords fournisseurs le permettent." },
];

const OFFRES: [string, string][] = [
  ["Découverte et tests IA", "Gratuit"],
  ["Atelier collectif IA", "29 à 79 €"],
  ["Consultation expert, 1 heure", "99 €"],
  ["Création de site internet", "Dès 490 €"],
  ["Accompagnement création d'entreprise", "Dès 990 €"],
  ["Abonnement Grafton IA professionnel", "1 500 à 3 000 € HT/mois"],
  ["Crédits IA / tokens", "Selon consommation"],
];

const PUBLICS = [
  ["Les particuliers", "qui veulent découvrir l'IA"],
  ["Les entrepreneurs", "qui veulent créer ou développer une activité"],
  ["Les entreprises", "qui veulent former leurs collaborateurs"],
];

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const els = root.querySelectorAll<HTMLElement>("[data-reveal]");
    if (!("IntersectionObserver" in window)) { els.forEach((e) => e.classList.add("vu")); return; }
    const io = new IntersectionObserver((es) => es.forEach((x) => { if (x.isIntersecting) { x.target.classList.add("vu"); io.unobserve(x.target); } }), { threshold: 0.12 });
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, []);
  return ref;
}

export default function EspacePhysique() {
  const ref = useReveal();
  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <DashboardSidebar />
        <main className="flex-1 min-w-0 overflow-x-hidden" style={{ background: "#0b0906", color: "#f3ede0" }}>
          <style>{`
            [data-reveal]{opacity:0;transform:translateY(22px);transition:opacity .9s ease,transform .9s ease}
            [data-reveal].vu{opacity:1;transform:none}
            @media (prefers-reduced-motion:reduce){[data-reveal]{opacity:1;transform:none;transition:none}}
            .ep-img{transition:transform 1.2s ease}.ep-card:hover .ep-img{transform:scale(1.04)}
          `}</style>
          <header className="h-12 flex items-center gap-3 px-4 border-b" style={{ borderColor: "rgba(212,175,55,.2)", background: "#0b0906" }}>
            <SidebarTrigger />
            <Link to="/laboratoire" className="text-xs inline-flex items-center gap-1" style={{ color: OR }}><ArrowLeft className="h-3.5 w-3.5" />Le laboratoire</Link>
          </header>

          <div ref={ref} data-testid="espace-physique">
            {/* HÉROS */}
            <section className="relative" style={{ minHeight: "78vh" }}>
              <img src={`${IMG}espace-facade.jpg`} alt="Façade du Grafton IA Store" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,9,6,.5) 0%, rgba(11,9,6,.3) 28%, rgba(11,9,6,.82) 52%, rgba(11,9,6,.97) 100%)" }} />
              <div className="relative max-w-5xl mx-auto px-6 md:px-10 flex flex-col justify-end" style={{ minHeight: "78vh", paddingBottom: "6vh" }}>
                <span data-reveal className="self-start text-[10px] tracking-[0.2em] px-3 py-1 rounded-full mb-5" style={{ border: "1px solid rgba(212,175,55,.5)", color: OR, background: "rgba(11,9,6,.55)" }}>LABORATOIRE · CONCEPT</span>
                <h1 data-reveal style={{ ...serif, fontSize: "clamp(38px,7vw,76px)", lineHeight: 1.03, fontWeight: 500, textShadow: "0 2px 24px rgba(0,0,0,.7)" }}>Grafton IA <em style={{ color: OR }}>The AI Experience Store</em></h1>
                <p data-reveal style={{ ...serif, fontSize: "clamp(18px,2.4vw,26px)", marginTop: 18, maxWidth: "34ch", opacity: 0.92 }}>Chez Apple, on découvre des produits. Chez Grafton IA, on vient <em style={{ color: OR }}>créer quelque chose</em>.</p>
              </div>
            </section>

            {/* CONCEPT */}
            <section className="max-w-4xl mx-auto px-6 md:px-10 py-16">
              <h2 data-reveal style={{ ...serif, fontSize: "clamp(28px,4vw,42px)", fontWeight: 500 }}>Un concept qui n'existe pas encore à grande échelle</h2>
              <p data-reveal className="mt-5 leading-relaxed" style={{ color: "rgba(243,237,224,.78)", fontSize: 17 }}>
                Un espace commercial physique entièrement consacré à l'intelligence artificielle, à l'entrepreneuriat et à l'exécution de projets. Il reprend les codes qui font le succès visuel des boutiques d'exception : grandes tables, écrans, architecture épurée, conseillers accessibles, et une expérience où les visiteurs peuvent tout essayer.
              </p>
              <p data-reveal className="mt-4 leading-relaxed" style={{ color: "rgba(243,237,224,.78)", fontSize: 17 }}>
                Un espace premium, ouvert et lumineux, avec une identité architecturale forte : grandes tables en bois clair, écrans intégrés, murs de démonstration, bar central et espaces d'accompagnement.
              </p>
            </section>

            {/* LES 6 ESPACES */}
            <section className="max-w-6xl mx-auto px-6 md:px-10 pb-6">
              <div data-reveal className="text-center mb-12">
                <span className="text-[11px] tracking-[0.2em]" style={{ color: OR }}>LES 6 ESPACES</span>
                <h2 style={{ ...serif, fontSize: "clamp(30px,4.5vw,48px)", fontWeight: 500, marginTop: 8 }}>Un parcours, de la découverte à la réalisation</h2>
              </div>
              <div className="space-y-14">
                {ESPACES.map((e, i) => (
                  <article key={e.n} data-reveal className="ep-card grid md:grid-cols-2 gap-6 md:gap-10 items-center">
                    <div className={`overflow-hidden rounded-2xl ${i % 2 ? "md:order-2" : ""}`} style={{ border: "1px solid rgba(212,175,55,.25)" }}>
                      {e.image && <img src={`${IMG}${e.image}`} alt={e.titre} loading="lazy" className="ep-img w-full h-full object-cover" style={{ aspectRatio: "3 / 2" }} />}
                    </div>
                    <div>
                      <div className="flex items-center gap-4 mb-3">
                        <div className="h-14 w-14" style={{ color: OR }}><Picto name={e.picto} /></div>
                        <span style={{ ...serif, color: OR, fontSize: 28, opacity: 0.8 }}>{e.n}</span>
                      </div>
                      <h3 style={{ ...serif, fontSize: 32, fontWeight: 500 }}>{e.titre}</h3>
                      <p className="mt-3 leading-relaxed" style={{ color: "rgba(243,237,224,.8)" }}>{e.texte}</p>
                      {e.legende && <p className="mt-3 text-xs italic" style={{ color: "rgba(212,175,55,.85)" }}>{e.legende}</p>}
                    </div>
                  </article>
                ))}
              </div>
            </section>

            {/* MODÈLE ÉCONOMIQUE */}
            <section className="max-w-4xl mx-auto px-6 md:px-10 py-16">
              <h2 data-reveal style={{ ...serif, fontSize: "clamp(28px,4vw,42px)", fontWeight: 500 }}>Comment Grafton IA gagnerait de l'argent</h2>
              <p data-reveal className="mt-4" style={{ color: "rgba(243,237,224,.78)" }}>L'accès à la boutique et aux démonstrations serait gratuit. Les revenus viendraient des prestations, des abonnements et des formations.</p>
              <div data-reveal className="mt-8 rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(212,175,55,.25)" }}>
                {OFFRES.map(([o, t], i) => (
                  <div key={o} className="flex items-center justify-between gap-4 px-5 py-4" style={{ background: i % 2 ? "rgba(212,175,55,.04)" : "transparent", borderTop: i ? "1px solid rgba(212,175,55,.15)" : "none" }}>
                    <span>{o}</span><span style={{ color: OR, fontWeight: 600, textAlign: "right" }}>{t}</span>
                  </div>
                ))}
              </div>
              <p data-reveal className="mt-4 text-xs" style={{ color: "rgba(243,237,224,.6)" }}>Tarifs indicatifs, à tester commercialement. La revente de crédits IA nécessiterait de vérifier les conditions des fournisseurs et les marges réelles.</p>
            </section>

            {/* POTENTIEL */}
            <section className="max-w-5xl mx-auto px-6 md:px-10 pb-16">
              <h2 data-reveal style={{ ...serif, fontSize: "clamp(28px,4vw,42px)", fontWeight: 500 }}>Le potentiel du concept</h2>
              <div className="mt-8 grid gap-4 md:grid-cols-3">
                {PUBLICS.map(([a, b]) => (
                  <div key={a} data-reveal className="rounded-2xl p-6" style={{ border: "1px solid rgba(212,175,55,.25)", background: "linear-gradient(160deg,#241d0e,#120f08)" }}>
                    <div style={{ ...serif, fontSize: 24, color: OR }}>{a}</div><p className="mt-1 text-sm" style={{ color: "rgba(243,237,224,.75)" }}>{b}</p>
                  </div>
                ))}
              </div>
              <p data-reveal className="mt-8 leading-relaxed" style={{ color: "rgba(243,237,224,.78)" }}>
                La boutique deviendrait aussi un lieu d'événements : démonstrations de nouveaux modèles, rencontres avec des créateurs de startups, conférences et lancements de solutions.
              </p>
              <p data-reveal className="mt-4 leading-relaxed" style={{ color: "rgba(243,237,224,.78)" }}>
                Le projet : un premier Grafton IA Store de 200 à 300 m², avec une architecture reproductible pour ouvrir ensuite plusieurs établissements.
              </p>
              <div data-reveal className="mt-8 rounded-2xl p-6" style={{ border: "1px solid rgba(212,175,55,.45)", background: "rgba(212,175,55,.06)" }}>
                <span className="text-[11px] tracking-[0.18em]" style={{ color: OR }}>PROCHAINE DÉCISION</span>
                <p className="mt-2" style={{ ...serif, fontSize: 24 }}>Quelle taille pour le premier espace : 100 m², 250 m² ou un flagship de 500 m² ?</p>
              </div>
            </section>

            <footer className="max-w-4xl mx-auto px-6 md:px-10 pb-14 text-xs leading-relaxed" style={{ color: "rgba(243,237,224,.5)" }}>
              Les visuels sont des illustrations du concept générées par intelligence artificielle. Les logos de tiers et les prix qui y figurent sont fictifs ; aucun partenariat ni accord avec ces marques n'est annoncé à ce jour.
            </footer>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
